import React, { ReactElement, ReactNode, createRef, useEffect, useRef, useState } from "react";
import { Table } from "react-bootstrap";
import { unstable_batchedUpdates } from "react-dom";
import { useTranslation } from "react-i18next";
import EmptyList from "go-core/components/EmptyList";
import { Loading } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as GripHorizontal } from "go-core/images/svg/grip-horizontal.svg";
import { ListParamsType } from "../../../list/services/types";
import { ListConfigColumn } from "../Column/services/types";
import { ReactComponent as SortDownSVG } from "./../../images/sort-down.svg";
import { ReactComponent as SortUpDownSVG } from "./../../images/sort-up-down.svg";
import { ReactComponent as SortUpSVG } from "./../../images/sort-up.svg";
import { MultiActionsBar, SingleActionsButton } from "./components/actions";
import { useDynamicTableHeight, useStickyColumns } from "./hooks";
import { GroupByConfig, ListConfigAction, ListConfigMultipleAction } from "./services/types";
import { getColumnsToDisplay, getItemStatus, getNewItemSortingColumns, getSortedColumns } from "./utils";

export interface ListTableProps {
	data: Array<any>;
	summaryData?: Record<string, any>;
	columns: ListConfigColumn[];
	actions?: ListConfigAction[];
	loading?: boolean;
	multipleActions?: ListConfigMultipleAction[];
	selectedColumns?: Array<string>;
	sortedColumns?: Array<string>;
	selectedSorts?: Array<string>;
	getStatus?: (dataItem: any) => "ENABLED" | "DISABLED" | "WARNING";
	onSort: (sort: string[]) => void;
	groupBy?: GroupByConfig;
	nested_config?: Record<string, any>;
	customTableRow?: ReactElement;
	selectedSegment?: string;
	numberOfActiveStickyColumns?: number;
	totalItemsCount?: number;
	params: ListParamsType;
	canManageMultiActionsForAllItems?: boolean;
	shouldFilterAvailableMultipleActions?: boolean;
	// the shouldFilterAvailableMultipleActions prop should be removed after configs in all components that use ListTable are updated (singleActions should contain all the available actions)
	doesIdColumnRedirectToPreviewPage?: boolean;
	// the doesIdColumnRedirectToPreviewPage prop should be removed after configs in all components that use ListTable are updated (render function of the id column should redirect on click to the preview page of the item)
	fetchData?: (params?: ListParamsType) => Promise<any>;
	underFiltersChildren?: ReactNode;
	toggleRow: (dataItem: Record<string, any>) => void;
	withSummary?: boolean;
}

const ListTable = (props: ListTableProps): JSX.Element => {
	const { t } = useTranslation();
	const [selectedItemsIds, setSelectedItemsIds] = useState<string[]>([]);
	const [areAllSelected, setAreAllSelected] = useState(false);
	const [areAllVisibleSelected, setAreAllVisibleSelected] = useState(false);
	const tableRef = useRef<HTMLTableElement>(null);
	const mainDivRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const lastColumnRef = useRef<HTMLTableCellElement>(null);
	const tableHeaderRowRef = useRef<HTMLTableRowElement>(null);
	const singleActions = props.actions;
	const multipleActions = props.multipleActions;
	const columnsToDisplay = getColumnsToDisplay(props.columns, props.selectedColumns);
	const sortedColumnsToDisplay = props.sortedColumns
		? getSortedColumns(columnsToDisplay, props.sortedColumns)
		: columnsToDisplay;
	const sortedColumnsToDisplayRefs = useRef(sortedColumnsToDisplay.map(() => createRef<HTMLTableCellElement>()));
	const isMobile = useWindowSize().isMobile;
	const { isTableHeightOverflowing, mainDivStyles } = useDynamicTableHeight(mainDivRef);

	const { getStickyCellStyles, areStickyColumnsActive, widthOfTheLastCell, StickyColumnsShadows } = useStickyColumns({
		numberOfActiveStickyColumns: props.numberOfActiveStickyColumns,
		numberOfColumns: sortedColumnsToDisplay.length,
		columnsOrder: props.sortedColumns || props.selectedColumns,
		isTableHeightOverflowing,
		columnsRefs: sortedColumnsToDisplayRefs,
		wrapperRef,
		mainDivRef,
		tableRef,
		lastColumnRef,
		insideStickyTable: props.underFiltersChildren !== undefined,
	});

	useEffect(() => {
		unstable_batchedUpdates(() => {
			setAreAllSelected(false);
			setAreAllVisibleSelected(false);
			setSelectedItemsIds([]);
		});
	}, [props.data, setSelectedItemsIds, setAreAllVisibleSelected, setAreAllSelected]);

	const toggleSelectAllVisible = () => {
		setSelectedItemsIds(areAllVisibleSelected ? [] : props.data.map((dataItem) => dataItem.id));
		setAreAllVisibleSelected(!areAllVisibleSelected);
		if (!areAllVisibleSelected) {
			window.dispatchEvent(new Event("RequestTotalItemsCountEvent"));
		}
	};

	const deselectAll = () => {
		setSelectedItemsIds([]);
		setAreAllVisibleSelected(false);
		setAreAllSelected(false);
	};

	const onChangeSelected = (itemId: string) => {
		setSelectedItemsIds((ids) => (ids.includes(itemId) ? ids.filter((item) => item !== itemId) : [...ids, itemId]));
		if (selectedItemsIds.length === 0) {
			window.dispatchEvent(new Event("RequestTotalItemsCountEvent"));
		}
	};

	const sortItemsByColumn = (columnId: string) => {
		const sortingColumnsIds = getNewItemSortingColumns(columnId, props.selectedSorts);
		props.onSort(sortingColumnsIds);
	};

	const isNested = (dataItem: any): boolean => {
		return (
			props.nested_config &&
			dataItem[`${props.nested_config.field}`] &&
			dataItem[`${props.nested_config.field}`].length > 0
		);
	};

	const getStatus = (dataItem: any) => {
		if (props.getStatus) {
			return props.getStatus(dataItem);
		}

		if (dataItem.status) {
			return getItemStatus(dataItem.status);
		}
	};

	const getTheadTopPosition = () => {
		const underFiltersContainer = document.querySelector(".under-filters-container");
		if (!underFiltersContainer) return undefined;

		const filtersSection = document.querySelector(".filters-section");

		if (!filtersSection) {
			if (isMobile) return "-1px";
			return undefined;
		}

		if (isMobile) {
			return filtersSection.clientHeight - 1;
		}

		const header = document.querySelector(".content-header");
		if (!header) return undefined;

		return filtersSection.clientHeight + header.clientHeight;
	};

	const getCustomFieldValue = (columnId: string, customFieldsRowValues: Record<string, any>[]) => {
		const key = columnId.substring(1);
		const customFieldConfig = props.params.customFields?.find((customField) => customField.id === columnId);
		let value = customFieldsRowValues?.find((f) => f.slug === key)?.value;
		if (!value) value = "";
		if (customFieldConfig?.type === "radio") {
			if (value === "true") value = t("common.word.yes", { ns: "lib" });
			else if (value === "false") value = t("common.word.no", { ns: "lib" });
		}

		return value;
	};

	return (
		<div className="table-wrapper" ref={wrapperRef}>
			{!props.underFiltersChildren && <StickyColumnsShadows />}
			<div className="table-list" ref={mainDivRef} style={mainDivStyles}>
				{props.data.length === 0 ? (
					<>
						{!props.loading ? (
							<EmptyList
								title={t("lib:go_list.empty_list.title")}
								description={t("lib:go_list.empty_list.description")}
							/>
						) : (
							<div className="list-loading">
								<Loading />
							</div>
						)}
					</>
				) : (
					<>
						<Table ref={tableRef}>
							{props.underFiltersChildren && <StickyColumnsShadows />}
							<thead style={{ top: getTheadTopPosition() }}>
								<tr ref={tableHeaderRowRef} data-testid="table-header-row">
									{multipleActions && multipleActions.length > 0 && (
										<th
											style={{
												width: "1%",
												...getStickyCellStyles(-1),
											}}
											ref={sortedColumnsToDisplayRefs.current[0]}
										>
											<input
												disabled={areAllSelected}
												type="checkbox"
												checked={areAllSelected || areAllVisibleSelected}
												onChange={toggleSelectAllVisible}
												data-testid="select-all-items-checkbox"
											/>
										</th>
									)}
									{sortedColumnsToDisplay.map((column, i) => {
										const isColumnACustomField = column.id.startsWith("+");
										let sortIcons;
										if (!column.disableSorting) {
											if (props.selectedSorts?.includes(column.id)) sortIcons = <SortUpSVG />;
											else if (props.selectedSorts?.includes(`-${column.id}`))
												sortIcons = <SortDownSVG />;
											else
												sortIcons = (
													<div className="ms-1">
														<SortUpDownSVG />
													</div>
												);
										}
										const tableHeaderContent = sortIcons ? (
											<div className="d-inline-flex">
												{column.name}
												{column.headerNameAfter && column.headerNameAfter()}
												{sortIcons}
											</div>
										) : (
											<>
												{column.name}
												{column.headerNameAfter && column.headerNameAfter()}
											</>
										);
										return (
											<th
												key={column.id}
												style={{
													...getStickyCellStyles(i),
													...column?.styleOverride,
													...column?.headerStyleOverride,
												}}
												onClick={
													column.disableSorting
														? undefined
														: () => sortItemsByColumn(column.id)
												}
												ref={sortedColumnsToDisplayRefs.current[i + 1]}
												data-testid={
													isColumnACustomField
														? `table-header-cell-custom-field-${i}`
														: `table-header-cell-${i}`
												}
											>
												{tableHeaderContent}
											</th>
										);
									})}
									{singleActions && singleActions.length > 0 && (
										<th
											ref={lastColumnRef}
											className={`last-empty-cell ${
												areStickyColumnsActive ? "sticky-last-empty-cell" : ""
											}`}
										/>
									)}
								</tr>
							</thead>
							{!props.loading && (
								<tbody data-testid="list-table-body">
									{multipleActions &&
										selectedItemsIds.length > 0 &&
										props.data.length > 0 &&
										mainDivRef.current && (
											<MultiActionsBar
												isButtonSticky={areStickyColumnsActive}
												numberOfColumns={sortedColumnsToDisplay.length}
												mainDivRef={mainDivRef}
												multiActions={multipleActions}
												singleActions={singleActions}
												data={props.data}
												selectedItems={selectedItemsIds}
												areAllSelected={areAllSelected}
												setAreAllSelected={setAreAllSelected}
												allItemsCount={props.totalItemsCount}
												groupBy={props.groupBy}
												isNested={isNested}
												nestedConfig={props.nested_config}
												params={props.params}
												canManageMultiActionsForAllItems={
													props.canManageMultiActionsForAllItems
												}
												shouldFilterAvailableMultipleActions={
													props.shouldFilterAvailableMultipleActions
												}
												clearSelected={deselectAll}
												widthOfTheLastCell={widthOfTheLastCell}
												tableHeaderRowRef={tableHeaderRowRef}
												fetchData={props.fetchData}
											/>
										)}
									{props.groupBy ? (
										<>
											{props.data.map((obj) => {
												const styleOverride = { ...getStickyCellStyles(0) };
												return (
													<React.Fragment key={obj.name}>
														<tr className="grouped-table-row" key={obj.name}>
															<td
																colSpan={
																	areStickyColumnsActive
																		? 0
																		: sortedColumnsToDisplay.length + 1
																}
																style={styleOverride}
															>
																{obj.name}
															</td>
														</tr>
														{obj.values.map((dataItem: any) => {
															const itemActions =
																singleActions !== undefined
																	? singleActions.filter((action) => {
																			return (
																				action.visible === undefined ||
																				action.visible(dataItem)
																			);
																	  })
																	: undefined;
															return (
																<tr key={dataItem.id}>
																	{
																		<>
																			{sortedColumnsToDisplay.map(
																				(column, index) => {
																					let overrideStyles = {
																						...column?.styleOverride,
																						...getStickyCellStyles(index),
																					};
																					if (column?.cellStyleOverride) {
																						overrideStyles = {
																							...overrideStyles,
																							...column?.cellStyleOverride(
																								dataItem
																							),
																						};
																					}

																					const value = dataItem[column.id];
																					if (column.render) {
																						return (
																							<td
																								id={`${column.id}[${dataItem.id}]`}
																								key={column.id}
																								style={overrideStyles}
																							>
																								{column.render(
																									dataItem
																								)}
																							</td>
																						);
																					}
																					if (typeof value === "object") {
																						return (
																							<td key={column.id}>
																								PROBLEM: OBJECT
																							</td>
																						);
																					}
																					return (
																						<td
																							id={`${column.id}[${dataItem.id}]`}
																							key={column.id}
																							style={overrideStyles}
																						>
																							{value}
																						</td>
																					);
																				}
																			)}
																			<SingleActionsButton
																				actions={itemActions}
																				item={dataItem}
																				mainDivRef={mainDivRef}
																				isButtonSticky={areStickyColumnsActive}
																				status={getStatus(dataItem)}
																				doesIdColumnRedirectToPreviewPage={
																					props.doesIdColumnRedirectToPreviewPage
																				}
																				params={props.params}
																				fetchData={props.fetchData}
																			/>
																		</>
																	}
																</tr>
															);
														})}
													</React.Fragment>
												);
											})}
										</>
									) : (
										<>
											{props.data.map((dataItem, index) => {
												const itemActions =
													singleActions !== undefined
														? singleActions.filter((action) => {
																return (
																	action.visible === undefined ||
																	action.visible(dataItem)
																);
														  })
														: undefined;
												return (
													<React.Fragment key={dataItem.id}>
														<tr data-testid={`table-row-${index}`}>
															{multipleActions && multipleActions.length > 0 && (
																<td
																	style={{
																		width: "1%",
																		...getStickyCellStyles(-1),
																	}}
																>
																	<input
																		disabled={areAllSelected}
																		type="checkbox"
																		onChange={() => onChangeSelected(dataItem.id)}
																		checked={
																			areAllSelected ||
																			selectedItemsIds.includes(dataItem.id)
																		}
																	/>
																</td>
															)}
															{sortedColumnsToDisplay.map((column, i) => {
																let overrideStyles = column?.cellStyleOverride
																	? {
																			...column?.styleOverride,
																			...column?.cellStyleOverride(dataItem),
																	  }
																	: { ...column?.styleOverride };
																overrideStyles = {
																	...getStickyCellStyles(i),
																	...overrideStyles,
																};

																if (column.id.startsWith("+")) {
																	const value = getCustomFieldValue(
																		column.id,
																		dataItem.custom_fields
																	);
																	return (
																		<td
																			id={`${column.id}[${dataItem.id}]`}
																			key={column.id}
																			style={overrideStyles}
																		>
																			{i === 0 && isNested(dataItem) && (
																				<GripHorizontal
																					onClick={() =>
																						props.toggleRow(dataItem)
																					}
																					style={{ cursor: "pointer" }}
																					id={`toggle-${dataItem.id}`}
																					className={"me-3"}
																				/>
																			)}
																			{value}
																		</td>
																	);
																}
																const value = dataItem[column.id];
																if (column.render) {
																	return (
																		<td
																			className={
																				column.id === "name"
																					? "list-image-name"
																					: ""
																			}
																			id={`${column.id}[${dataItem.id}]`}
																			key={column.id}
																			style={overrideStyles}
																		>
																			{i === 0 && isNested(dataItem) ? (
																				<div className="d-flex align-items-center">
																					<GripHorizontal
																						onClick={() =>
																							props.toggleRow(dataItem)
																						}
																						style={{ cursor: "pointer" }}
																						id={`toggle-${dataItem.id}`}
																						className={"me-3"}
																						data-testid="grip-horizontal"
																					/>
																					{column.render(dataItem)}
																				</div>
																			) : (
																				<>{column.render(dataItem)}</>
																			)}
																		</td>
																	);
																}
																if (typeof value === "object") {
																	return (
																		<td key={column.id} style={overrideStyles}>
																			PROBLEM: OBJECT
																		</td>
																	);
																}
																return (
																	<td
																		id={`${column.id}[${dataItem.id}]`}
																		key={column.id}
																		style={overrideStyles}
																	>
																		{i === 0 && isNested(dataItem) && (
																			<GripHorizontal
																				onClick={() =>
																					props.toggleRow(dataItem)
																				}
																				style={{ cursor: "pointer" }}
																				id={`toggle-${dataItem.id}`}
																				className={"me-3"}
																			/>
																		)}
																		{value}
																	</td>
																);
															})}
															<SingleActionsButton
																actions={itemActions}
																item={dataItem}
																mainDivRef={mainDivRef}
																isButtonSticky={areStickyColumnsActive}
																status={getStatus(dataItem)}
																doesIdColumnRedirectToPreviewPage={
																	props.doesIdColumnRedirectToPreviewPage
																}
																params={props.params}
																fetchData={props.fetchData}
															/>
														</tr>
														{isNested(dataItem) &&
															dataItem[`${props.nested_config?.field}`].map(
																(item: any, index: number) => {
																	const nestedItemActions =
																		singleActions !== undefined
																			? singleActions.filter((action) => {
																					return (
																						action.visible === undefined ||
																						action.visible(item)
																					);
																			  })
																			: undefined;
																	return (
																		<tr
																			key={item.id}
																			id={`toggled[${dataItem.id}].nested[${index}]`}
																			style={{ display: "none" }}
																		>
																			{sortedColumnsToDisplay.map((column, i) => {
																				let overrideStyles = {
																					...column?.styleOverride,
																					...getStickyCellStyles(i),
																				};
																				if (column?.cellStyleOverride)
																					overrideStyles = {
																						...overrideStyles,
																						...column?.cellStyleOverride(
																							dataItem
																						),
																					};
																				if (column.id.startsWith("+")) {
																					const key = column.id.substring(1);
																					let value = item.custom_fields
																						? item.custom_fields[key]
																						: undefined;
																					if (!value) value = "";
																					return (
																						<td
																							style={
																								i === 0
																									? {
																											paddingLeft:
																												"48px",
																											...overrideStyles,
																									  }
																									: {
																											...overrideStyles,
																									  }
																							}
																							id={`nested_${column.id}[${item.id}]`}
																							key={column.id}
																						>
																							{value}
																						</td>
																					);
																				}
																				const value = item[column.id];
																				if (column.render) {
																					return (
																						<td
																							style={
																								i === 0
																									? {
																											paddingLeft:
																												"48px",
																											...overrideStyles,
																									  }
																									: {
																											...overrideStyles,
																									  }
																							}
																							className={
																								column.id === "name"
																									? "list-image-name"
																									: ""
																							}
																							id={`nested_${column.id}[${item.id}]`}
																							key={column.id}
																						>
																							{column.render(item)}
																						</td>
																					);
																				}
																				if (typeof value === "object") {
																					return (
																						<td
																							style={
																								i === 0
																									? {
																											paddingLeft:
																												"48px",
																											...overrideStyles,
																									  }
																									: {
																											...overrideStyles,
																									  }
																							}
																							key={column.id}
																						>
																							PROBLEM: OBJECT
																						</td>
																					);
																				}
																				return (
																					<td
																						style={
																							i === 0
																								? {
																										paddingLeft:
																											"48px",
																										...overrideStyles,
																								  }
																								: {
																										...overrideStyles,
																								  }
																						}
																						id={`nested_${column.id}[${item.id}]`}
																						key={column.id}
																					>
																						{value}
																					</td>
																				);
																			})}
																			<SingleActionsButton
																				actions={nestedItemActions}
																				item={item}
																				mainDivRef={mainDivRef}
																				isButtonSticky={areStickyColumnsActive}
																				status={getStatus(dataItem)}
																				doesIdColumnRedirectToPreviewPage={
																					props.doesIdColumnRedirectToPreviewPage
																				}
																				params={props.params}
																				fetchData={props.fetchData}
																			/>
																		</tr>
																	);
																}
															)}
													</React.Fragment>
												);
											})}
											{props.customTableRow}
											{props.withSummary && (
												<tr data-testid="list-table-summary-row">
													{sortedColumnsToDisplay.map((column, index) => {
														return (
															<td
																key={`summary-${column.id}`}
																style={{
																	...getStickyCellStyles(index),
																	textAlign: "right",
																}}
															>
																{props.summaryData && column.renderSummary
																	? column.renderSummary(props.summaryData)
																	: ""}
															</td>
														);
													})}
												</tr>
											)}
										</>
									)}
								</tbody>
							)}
						</Table>
						{props.loading && (
							<div className="list-loading">
								<Loading />
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default ListTable;
